import React from "react";
import { formatMessage } from "lib/globalize-util";
import {
    mergeStyleSets,
    Label,
    ITag,
    Callout,
    DirectionalHint,
    TagPicker,
    Target,
    IPickerItemProps,
} from "@nuorder/components";
import { FOLDERS } from "constants/integration/folders";
import { IBrand } from "containers/folders/types/brands";
import { SpaceView } from "containers/folders/hooks/utils";
import { Capabilities, ItemKind } from "containers/folders/types/base";
import { IFolderSelection } from "../folder-selection/folder-navigation";
import { useFolderSelection } from "../folder-selection/use-folder-selection";
import { buildRootDrive } from "containers/folders/utils/drive-navigation";
import { SHARED_WITH_ME_ROOT_ITEM_ID } from "containers/folders/utils/constants";
import { ManageDriveItemSelection } from "../bulk-collector/manage-drive-item-selection";
import { AssortmentPickerTag } from "./assortment-picker-tag";
import { processAssortmentItem } from "../utils/process-assortment-items";

interface IOwnProps {
    itemsSelected: nuorder.folder.v1.Item[];
    mySpace: nuorder.folder.v1.Space;
    merchantSpace: nuorder.folder.v1.Space;
    displayMerchantDrive: boolean;
    brand?: IBrand;
    membership: nuorder.merchant.v1.Member;
    spaceView: SpaceView;
    spaceItemNavigation?: Partial<nuorder.folder.v1.Item>[];
    parentId: string;
    itemLimit: number;
    onChangeSelection: (items: nuorder.folder.v1.Item[]) => void;
    displayMyDrive: boolean;
    verticalAssortmentsEnabled: boolean;
}

export const AssortmentPicker: React.FunctionComponent<IOwnProps> = ({
    itemsSelected = [],
    mySpace,
    merchantSpace,
    displayMerchantDrive,
    brand,
    membership,
    spaceView,
    spaceItemNavigation,
    parentId,
    itemLimit,
    onChangeSelection,
    displayMyDrive,
    verticalAssortmentsEnabled,
}) => {
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                callout: {
                    width: 420,
                    maxWidth: "calc(100vw - 16px)",
                    selectors: {
                        ".ms-Callout-main": {
                            padding: 0,
                        },
                    },
                },
            }),
        []
    );

    const space =
        spaceView === SpaceView.MerchantDrive ? merchantSpace : spaceView === SpaceView.MyDrive ? mySpace : null;
    const [settings, setSettings] = useFolderSelection({
        multiple: true,
        automaticLoad: false,
        currentItemId: parentId,
        rootItemId: spaceView !== SpaceView.SharedWithMe ? space?.root_item_id : SHARED_WITH_ME_ROOT_ITEM_ID,
        currentView: spaceView,
        navigation: [
            buildRootDrive(space, spaceView, membership?.merchant_ref?.name ?? membership?.merchant_id),
            ...((spaceItemNavigation || []) as IFolderSelection[]),
        ],
        itemSelected: itemsSelected as IFolderSelection[],
    });
    const [anchorRef, setAnchorRef] = React.useState<EventTarget>(undefined);

    const processedAssortments = itemsSelected.map((item) => processAssortmentItem(item, verticalAssortmentsEnabled));

    const handleShowPopover = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            setAnchorRef(event.target);
        },
        [setAnchorRef]
    );

    const handleClosePopover = React.useCallback(
        (event) => {
            setSettings({
                multiple: true,
                automaticLoad: false,
                currentItemId: parentId,
                rootItemId: spaceView !== SpaceView.SharedWithMe ? space?.root_item_id : SHARED_WITH_ME_ROOT_ITEM_ID,
                currentView: spaceView,
                navigation: [
                    buildRootDrive(space, spaceView, membership?.merchant_ref?.name ?? membership?.merchant_id),
                    ...((spaceItemNavigation || []) as IFolderSelection[]),
                ],
                itemSelected: itemsSelected as IFolderSelection[],
                markAllAsSelected: false,
            });

            event.preventDefault();
            setAnchorRef(undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [parentId, spaceView, space, spaceItemNavigation, itemsSelected, setSettings]
    );

    const handleChangeAssortments = React.useCallback(
        (tags: ITag[]) => {
            const items = (settings.itemSelected as IFolderSelection[]).filter((it) =>
                tags.some((t) => t.key === it.id)
            );
            setSettings({
                itemSelected: items,
            });
            onChangeSelection(items);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChangeSelection, settings]
    );

    // this is just because the tagpicker has the `onResolveSuggestions` as required, but that event is not needed in here
    const handleResolveSuggestions = React.useCallback((_filterText: string) => null, []);

    const handleRenderTagPickerItem = React.useCallback((props: IPickerItemProps<any>) => {
        if (props?.item) {
            return <AssortmentPickerTag {...props} />;
        }
        return null;
    }, []);

    return (
        <>
            <div>
                <Label required>{formatMessage("Choose assortments:")}</Label>
                <TagPicker
                    selectedItems={processedAssortments}
                    onResolveSuggestions={handleResolveSuggestions}
                    onChange={handleChangeAssortments}
                    inputProps={
                        {
                            placeholder: formatMessage("Click to select up to |limit| assortments", {
                                limit: itemLimit,
                            }),
                            onKeyPress: (e) => e.preventDefault(),
                            onClick: handleShowPopover,
                            "data-test": FOLDERS.CREATE_ROLLUP_ASSORTMENT_DIALOG.ASSORTMENT_PICKER,
                        } as any
                    }
                    pickerSuggestionsProps={{
                        noResultsFoundText: formatMessage("No results found"),
                    }}
                    onRenderItem={handleRenderTagPickerItem}
                    loadingText={formatMessage("Loading...")}
                />
            </div>
            {anchorRef && (
                <Callout
                    className={classes.callout}
                    directionalHint={DirectionalHint.bottomCenter}
                    gapSpace={5}
                    target={anchorRef as Target}
                    onDismiss={handleClosePopover}
                >
                    <ManageDriveItemSelection
                        onChangeSelection={onChangeSelection}
                        onClose={handleClosePopover}
                        onUpdateSettings={setSettings}
                        settings={settings}
                        displayMerchantDrive={displayMerchantDrive}
                        membership={membership}
                        mySpace={mySpace}
                        merchantSpace={merchantSpace}
                        item={null}
                        displayMyDrive={displayMyDrive}
                        itemLimit={itemLimit}
                        itemKind={ItemKind.CollectionDocument}
                        itemType={Capabilities.Assortments}
                    />
                </Callout>
            )}
        </>
    );
};
